import React, { useState } from 'react';
import useForm from '../../hooks/useForm';
import FormField from '../common/FormField';
import useAuth from '../../hooks/useAuth';
import './DecisionForm.css';

// Values mirror the backend exactly (DecisionRequest / config/tenantverify.php), not the display
// labels — see utils/applicationStatus.js for the same codes shown read-only elsewhere.
const OUTCOMES = [
  { value: 'shortlist', label: 'Shortlist' },
  { value: 'approve', label: 'Approve' },
  { value: 'decline', label: 'Decline' },
  { value: 'request_information', label: 'Request more information' }
];

// Mirrors config('tenantverify.decision_reason_codes') / decision_reason_codes_by_outcome exactly —
// the backend rejects a reason code that doesn't belong to the chosen outcome (an "approve" citing
// "Affordability criteria not met" is a contradiction, not just mismatched labels).
const REASON_LABELS = {
  AFF010: 'Affordability criteria met',
  CRE010: 'Credit profile acceptable',
  REF010: 'References favourable',
  INC010: 'Income verified',
  ID010: 'Identity verified',
  AFF001: 'Affordability criteria not met',
  CRE002: 'Credit profile unacceptable',
  REF001: 'Unfavourable references received',
  INC001: 'Income unverifiable',
  ID001: 'Identity could not be verified',
  MOR001: 'Additional information or documents required',
  OTH001: 'Other (see notes)',
};

const REASON_CODES_BY_OUTCOME = {
  approve: ['AFF010', 'CRE010', 'REF010', 'INC010', 'ID010', 'OTH001'],
  shortlist: ['AFF010', 'CRE010', 'REF010', 'INC010', 'ID010', 'OTH001'],
  decline: ['AFF001', 'CRE002', 'REF001', 'INC001', 'ID001', 'OTH001'],
  request_information: ['MOR001', 'INC001', 'ID001', 'REF001', 'OTH001'],
};

const ADVERSE = ['decline', 'request_information'];

const DecisionForm = ({ onSubmit, isSubmitting }) => {
  const { user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);

  // The backend requires at least one reason code for every decision, not only adverse ones — a
  // record of "approved because X" matters as much as "declined because Y" for the audit trail.
  const validate = (vals) => {
    const errs = {};
    if (!vals.outcome) errs.outcome = 'Outcome is required.';
    if (vals.reasons.length === 0) errs.reasons = 'At least one reason is required, whatever the outcome.';
    if (vals.notes && vals.notes.length > 500) errs.notes = 'Keep this to 500 characters.';
    return errs;
  };

  const { values, errors, handleChange, handleSubmit, setFieldValue } = useForm({
    outcome: '',
    reasons: [],
    notes: '',
    conditions: ''
  }, validate);

  const onOutcomeChange = (e) => {
    handleChange(e);
    setShowWarning(ADVERSE.includes(e.target.value));
    // Reasons that applied to the previous outcome may not apply to the new one — e.g. switching from
    // "Decline" to "Approve" must not silently submit "Affordability criteria not met" as if it were a
    // reason to approve.
    setFieldValue('reasons', []);
  };

  const handleReasonToggle = (val) => {
    const newReasons = values.reasons.includes(val)
      ? values.reasons.filter((r) => r !== val)
      : [...values.reasons, val];
    setFieldValue('reasons', newReasons);
  };

  const submit = (vals) => onSubmit({
    outcome: vals.outcome,
    reason_codes: vals.reasons,
    reason_notes: vals.notes || undefined,
    conditions: vals.outcome === 'approve' && vals.conditions ? vals.conditions : undefined
  });

  return (
    <form className="decision-form card" onSubmit={handleSubmit(submit)}>
      <div className="card-header bg-primary text-white"><h2>Record Final Decision</h2></div>
      <div className="card-body">
        <FormField label="Decision Maker" name="decisionMaker" value={user?.first_name ? `${user.first_name} ${user.last_name}` : (user?.email || '')} disabled />

        <div className="form-group">
          <label className="form-label">Outcome</label>
          <div className="radio-group">
            {OUTCOMES.map(({ value, label }) => (
              <label key={value} className="radio-label">
                <input type="radio" name="outcome" value={value} checked={values.outcome === value} onChange={onOutcomeChange} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {errors.outcome && <p className="error-text">{errors.outcome}</p>}
        </div>

        {showWarning && (
          <div className="decision-warning">
            <strong>Notice:</strong> The applicant will be notified of this adverse decision and its material reasons. They retain the right to dispute.
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Reasons</label>
          {values.outcome ? (
            <div className="checkbox-group">
              {REASON_CODES_BY_OUTCOME[values.outcome].map((code) => (
                <label key={code} className="checkbox-label">
                  <input type="checkbox" checked={values.reasons.includes(code)} onChange={() => handleReasonToggle(code)} />
                  <span>{REASON_LABELS[code]}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="ap-muted">Choose an outcome first.</p>
          )}
          {errors.reasons && <p className="error-text">{errors.reasons}</p>}
        </div>

        <FormField label="Notes (internal, max 500 characters)" name="notes" type="textarea" value={values.notes} onChange={handleChange} maxLength="500" error={errors.notes} />
        {values.outcome === 'approve' && <FormField label="Conditions (optional)" name="conditions" type="textarea" value={values.conditions} onChange={handleChange} />}

        <div className="decision-actions">
          <button type="submit" className="btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Recording…' : 'Confirm Decision'}</button>
        </div>
      </div>
    </form>
  );
};
export default DecisionForm;
